// Drawing a bent line on the ground for a vertical profile, and showing a profile's line, its corridor and the
// profile cursor in the 3D view.  Mixed into Viewer (see the end of viewer.js).
import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

const LINE = 0x8b5cf6, CORR = 0xc4b5fd, CURSOR = 0xffd23f;

export const ProfileMixin = {
  // ---------------------------------------------------------------- drawing
  // cb.change(points) after every edit, cb.done(points) on double-click / Enter
  startProfileLine(points, cb) {
    this.lineDraw = { pts: (points || []).map(p => [p[0], p[1]]), cb, rubber: null };
    this.canvas.style.cursor = 'crosshair';
    if (!this.lineMoveHook) {
      this.lineMoveHook = true;
      this.canvas.addEventListener('pointermove', (e) => {
        if (!this.lineDraw || !this.lineDraw.pts.length) return;
        const now = performance.now(); if (now - (this.lineMoveAt || 0) < 40) return; this.lineMoveAt = now;
        const g = this.groundRay(e.clientX, e.clientY); if (!g) return;
        const p = this.fromLocal(g); this.lineDraw.rubber = [p.easting, p.northing]; this.drawProfileLine();
      });
      this.canvas.addEventListener('dblclick', (e) => {
        if (!this.lineDraw) return;
        e.preventDefault();
        const P = this.lineDraw.pts;            // the double-click's two clicks added the same spot twice
        while (P.length > 1 && Math.hypot(P[P.length - 1][0] - P[P.length - 2][0], P[P.length - 1][1] - P[P.length - 2][1]) < 2) P.pop();
        this.finishProfileLine();
      });
    }
    this.drawProfileLine(); this.lineDraw.cb?.change?.(this.lineDraw.pts.slice());
  },

  addProfilePoint(x, y) {
    const D = this.lineDraw; if (!D) return;
    const i = this.nearestStation(x, y, 12);          // snap to a survey station, to follow a passage
    let p;
    if (i >= 0) { const s = this.stationInfo(i); p = [s.easting, s.northing]; }
    else { const g = this.groundRay(x, y); if (!g) return; const q = this.fromLocal(g); p = [q.easting, q.northing]; }
    D.pts.push([Math.round(p[0] * 100) / 100, Math.round(p[1] * 100) / 100]);
    this.drawProfileLine(); D.cb?.change?.(D.pts.slice());
  },

  undoProfilePoint() { const D = this.lineDraw; if (!D) return; D.pts.pop(); this.drawProfileLine(); D.cb?.change?.(D.pts.slice()); },

  finishProfileLine() {
    const D = this.lineDraw; if (!D) return null;
    this.lineDraw = null; this.canvas.style.cursor = '';
    this.drawProfileLine();
    D.cb?.done?.(D.pts.slice());
    return D.pts;
  },

  cancelProfileLine() { if (!this.lineDraw) return; this.lineDraw = null; this.canvas.style.cursor = ''; this.drawProfileLine(); },

  // ---------------------------------------------------------------- display
  // the saved / current line: {line: [[E, N], ...], corridor: m} or null
  setProfileLine(spec) { this.profileSpec = spec; this.drawProfileLine(); },

  drapedPositions(pts, offset = 0, lift = 1.5) {
    // pts [[E, N]...] -> local positions following the ground; offset = sideways shift (m, + to the right)
    if (!this.origin || pts.length < 2) return [];
    const [xc, yc, zc] = this.origin, out = [];
    for (let k = 0; k < pts.length - 1; k++) {
      let [ax, ay] = pts[k], [bx, by] = pts[k + 1];
      const L = Math.hypot(bx - ax, by - ay); if (L < 0.01) continue;
      if (offset) { const nx = (by - ay) / L * offset, ny = -(bx - ax) / L * offset; ax += nx; bx += nx; ay += ny; by += ny; }
      const n = Math.max(1, Math.min(400, Math.ceil(L / 4)));
      for (let i = 0; i < n; i++) {
        const t0 = i / n, t1 = (i + 1) / n;
        for (const t of [t0, t1]) {
          const E = ax + (bx - ax) * t, N = ay + (by - ay) * t;
          out.push(E - xc, this.groundAt(E, N) - zc + lift, -(N - yc));
        }
      }
    }
    return out;
  },

  lineMat(color, width, opacity = 1) {
    const m = new LineMaterial({ color, linewidth: width, depthTest: false, transparent: true, opacity });
    m.resolution.set(innerWidth, innerHeight); this.lineMats.push(m); return m;
  },

  drawProfileLine() {
    if (this.profileObj) {
      this.world.remove(this.profileObj);
      this.profileObj.traverse(o => { o.geometry?.dispose(); if (o.material && o.material !== this.cursorMat) { this.lineMats = this.lineMats.filter(m => m !== o.material); o.material.dispose(); } });
      this.profileObj = null;
    }
    const labels = [];
    const D = this.lineDraw, S = this.profileSpec;
    const pts = D ? D.pts.concat(D.rubber && D.pts.length ? [D.rubber] : []) : S?.line || [];
    if (this.origin && pts.length) {
      const grp = new THREE.Group(), [xc, yc, zc] = this.origin;
      const add = (arr, mat, order) => { if (arr.length < 6) return; const g = new LineSegmentsGeometry(); g.setPositions(arr); const l = new LineSegments2(g, mat); l.renderOrder = order; grp.add(l); };
      add(this.drapedPositions(pts), this.lineMat(LINE, 3.2), 46);
      if (!D && S?.corridor) for (const side of [-1, 1]) add(this.drapedPositions(pts, side * S.corridor, 1), this.lineMat(CORR, 1.2, 0.7), 45);
      const corners = (D ? D.pts : pts).map(([E, N]) => new THREE.Vector3(E - xc, this.groundAt(E, N) - zc + 2, -(N - yc)));
      const dots = new THREE.Points(new THREE.BufferGeometry().setFromPoints(corners),
        new THREE.PointsMaterial({ color: 0xffffff, size: 7, sizeAttenuation: false, depthTest: false }));
      dots.renderOrder = 47; grp.add(dots);
      if (!D && corners.length > 1) {
        const ends = S?.ends || ['A', 'A′'];
        labels.push({ text: ends[0], x: corners[0].x, y: corners[0].y + 12, z: corners[0].z });
        const e = corners[corners.length - 1]; labels.push({ text: ends[1], x: e.x, y: e.y + 12, z: e.z });
      }
      this.world.add(grp); this.profileObj = grp;
    }
    this.labels.setProfile(labels);
    this.dirty = true;
  },

  // where the cursor is on the profile: a ball at (E, N, Z) and a plumb line up to the ground
  setProfileCursor(E, N, Z) {
    if (!this.cursorObj) {
      const g = new THREE.Group();
      this.cursorMat = this.cursorMat || this.lineMat(CURSOR, 2, 0.95);
      const ball = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 12), new THREE.MeshBasicMaterial({ color: CURSOR, depthTest: false }));
      ball.renderOrder = 48; g.add(ball);
      const plumb = new LineSegments2(new LineSegmentsGeometry(), this.cursorMat); plumb.renderOrder = 48; g.add(plumb);
      g.userData = { ball, plumb }; this.cursorObj = g; this.world.add(g);
    }
    const g = this.cursorObj;
    if (E == null || !this.origin) { g.visible = false; this.dirty = true; return; }
    const [xc, yc, zc] = this.origin, gz = this.groundAt(E, N);
    const x = E - xc, z = -(N - yc), y = (Z ?? gz) - zc;
    g.visible = true;
    const r = Math.max(1.5, this.camera.position.distanceTo(this.controls.target) * 0.006);
    g.userData.ball.position.set(x, y, z); g.userData.ball.scale.set(r, r / this.opts.ve, r);
    g.userData.plumb.geometry.setPositions([x, gz - zc + 2, z, x, y, z]);
    this.dirty = true;
  },
};
